import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import { Trans } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Section from '../../componentsv2/Section';
import Skeleton from '../../componentsv2/Skeleton';
import StatusTracker, { StatusTrackerTone } from '../../componentsv2/StatusTrackerv2';
import Typography from '../../componentsv2/Typography';
import Button from '../../componentsv2/Button';
import Chip from '../../componentsv2/Chip';
import { CheckboxField } from '../../componentsv2/Field';
import { BuildingsIcon, ClipboardTickIcon, LargeBankingIcon, MiniInboxIcon } from '../../components/Icons';
import { startBanking } from '../../services/banking';
import { useTranslation } from '../../hooks/i18n';
import { useSnackbar } from '../../context/snackbar';
import { ACTIVATION, BANKING_KYC_ONBOARDING } from '../../constants/auth';
import { documentUrlByCountry } from '../../constants/banking';
import { MX_COUNTRY_CODE } from '../../constants/data';
import { bankingEvents } from '../../constants/events';
import { ConnectedAccountStatus } from '../../types/banking';
import {
  Company,
  OnboardingStage,
  OnboardingTrackerStep,
  ProductStatus,
  SatCredentialStatus,
} from '../../types/companyUser';
import { getOnboardingStage } from '../../utils/onboarding';
import { COLORS } from '../../themev2';

const TRACKER_STEP_BY_STAGE: Record<OnboardingStage, OnboardingTrackerStep> = {
  [OnboardingStage.Screening]: OnboardingTrackerStep.Setup,
  [OnboardingStage.ManualReview]: OnboardingTrackerStep.Setup,
  [OnboardingStage.Application]: OnboardingTrackerStep.Application,
  [OnboardingStage.Verification]: OnboardingTrackerStep.Verification,
  [OnboardingStage.ActionRequired]: OnboardingTrackerStep.Verification,
  [OnboardingStage.Terms]: OnboardingTrackerStep.Start,
  [OnboardingStage.Active]: OnboardingTrackerStep.Start,
};

const STAGE_CONTENT_KEYS: Record<OnboardingStage, { title: string; subtitle: string; description: string }> =
  {
    [OnboardingStage.Screening]: {
      title: 'banking-page:onboardingTracker.setup.title',
      subtitle: 'banking-page:onboardingTracker.screening.subtitle',
      description: 'banking-page:onboardingTracker.screening.description',
    },
    [OnboardingStage.ManualReview]: {
      title: 'banking-page:onboardingTracker.setup.title',
      subtitle: 'banking-page:onboardingTracker.manualReview.subtitle',
      description: 'banking-page:onboardingTracker.manualReview.description',
    },
    [OnboardingStage.Application]: {
      title: 'banking-page:bankingApplication.info.application.title',
      subtitle: 'banking-page:onboardingTracker.application.subtitle',
      description: 'banking-page:onboardingTracker.application.description',
    },
    [OnboardingStage.Verification]: {
      title: 'banking-page:onboardingTracker.verification.title',
      subtitle: 'banking-page:onboardingTracker.verification.subtitle',
      description: 'banking-page:bankingApplication.info.review.description',
    },
    [OnboardingStage.ActionRequired]: {
      title: 'banking-page:bankingApplication.info.review.title',
      subtitle: 'banking-page:bankingApplication.info.action_required.subtitle',
      description: 'banking-page:bankingApplication.info.action_required.description',
    },
    [OnboardingStage.Terms]: {
      title: 'banking-page:bankingApplication.info.banking.title',
      subtitle: 'banking-page:bankingApplication.info.banking.subtitle',
      description: 'banking-page:bankingApplication.info.banking.description',
    },
    [OnboardingStage.Active]: {
      title: 'banking-page:bankingApplication.info.banking.title',
      subtitle: 'banking-page:bankingApplication.info.banking.subtitle',
      description: 'banking-page:bankingApplication.info.banking.description',
    },
  };

const PREPARING_APPLICATION_CONTENT_KEYS = {
  title: 'banking-page:bankingApplication.info.application.title',
  subtitle: 'banking-page:onboardingTracker.preparingApplication.subtitle',
  description: 'banking-page:onboardingTracker.preparingApplication.description',
};

const SAT_NOTICE_KEYS: Partial<Record<SatCredentialStatus, string>> = {
  [SatCredentialStatus.Pending]: 'banking-page:onboardingTracker.satPending.description',
  [SatCredentialStatus.Invalid]: 'banking-page:onboardingTracker.satInvalid.description',
};

const TermsLink = ({ href, children }: { href: string; children?: React.ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    style={{ color: COLORS.indigo[500], fontWeight: 600 }}
    onClick={() => logEvent(bankingEvents.CLICK_TERMS_AND_CONDITIONS)}
  >
    {children}
  </a>
);

type OnboardingTrackerProps = {
  company: Company;
  bankingStatus?: ConnectedAccountStatus;
  isLoading?: boolean;
  onStart: (updatedProductStatus: ProductStatus) => void;
};

const OnboardingTracker = ({
  company,
  bankingStatus,
  isLoading = false,
  onStart,
}: OnboardingTrackerProps) => {
  const { t } = useTranslation(['banking-page', 'activation']);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isStartingBanking, setIsStartingBanking] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const stage = getOnboardingStage(company, bankingStatus);
  const isActionRequired = stage === OnboardingStage.ActionRequired;
  const isPreparingApplication = stage === OnboardingStage.Application && !company.accounts?.length;
  const content = isPreparingApplication ? PREPARING_APPLICATION_CONTENT_KEYS : STAGE_CONTENT_KEYS[stage];
  const isMexican = company.country === MX_COUNTRY_CODE;
  const termsUrl = isMexican ? documentUrlByCountry.MX : documentUrlByCountry.default;
  const satCredentialStatus = company.productStatus?.satCredentialStatus;
  const satNoticeKey =
    stage === OnboardingStage.Screening && isMexican && satCredentialStatus
      ? SAT_NOTICE_KEYS[satCredentialStatus]
      : undefined;
  const isSatInvalid = !!satNoticeKey && satCredentialStatus === SatCredentialStatus.Invalid;

  const statuses = [
    {
      value: OnboardingTrackerStep.Setup,
      label: t('banking-page:onboardingTracker.tracker.setup'),
      icon: <BuildingsIcon />,
    },
    {
      value: OnboardingTrackerStep.Application,
      label: t('banking-page:statusTracker.application.label'),
      icon: <MiniInboxIcon />,
    },
    {
      value: OnboardingTrackerStep.Verification,
      label: t('banking-page:onboardingTracker.tracker.verification'),
      icon: (
        <ClipboardTickIcon
          color={isActionRequired ? COLORS.orange[900] : COLORS.gray[1000]}
          pathOpacity={0.4}
        />
      ),
      caption: isActionRequired ? t('banking-page:onboardingTracker.actionRequired') : undefined,
      tone: isActionRequired ? StatusTrackerTone.Warning : undefined,
    },
    {
      value: OnboardingTrackerStep.Start,
      label: t('banking-page:onboardingTracker.tracker.start'),
      icon: (
        <LargeBankingIcon width={25} height={24} color={COLORS.gray[1000]} circleColor={COLORS.brand.white} />
      ),
    },
  ];

  const handleContinueApplication = () => {
    logEvent(bankingEvents.CLICK_CONTINUE_APPLICATION, { stage });
    navigate(BANKING_KYC_ONBOARDING);
  };

  const handleUpdateSatCredentials = () => {
    showSnackbar(t('activation:mxSat.invalidCredential'), 'error');
    navigate(ACTIVATION, { state: { satRfc: company.taxId } });
  };

  const handleStartBanking = () => {
    logEvent(bankingEvents.CLICK_START_BANKING);
    setIsStartingBanking(true);
    startBanking(company.id, company.productStatus.id)
      .then(onStart)
      .finally(() => setIsStartingBanking(false));
  };

  const getAction = () => {
    if (isSatInvalid) {
      return {
        label: t('banking-page:onboardingTracker.satInvalid.cta'),
        onClick: handleUpdateSatCredentials,
      };
    }
    if (stage === OnboardingStage.Application && !isPreparingApplication) {
      return {
        label: t('banking-page:onboardingTracker.application.cta'),
        onClick: handleContinueApplication,
      };
    }
    if (isActionRequired) {
      return { label: t('banking-page:bankingApplication.resumeBtn'), onClick: handleContinueApplication };
    }
    return null;
  };
  const action = getAction();

  if (isLoading) {
    return (
      <Grid item xs={12}>
        <Skeleton height={240} />
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Section>
        <Grid container rowSpacing={3}>
          <Grid item xs={12}>
            <StatusTracker currentStatus={TRACKER_STEP_BY_STAGE[stage]} statuses={statuses} />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ fontWeight: 600, color: COLORS.cian[700] }}>{t(content.title)}</Typography>
                {isActionRequired && (
                  <Chip
                    size="small"
                    label={t('banking-page:onboardingTracker.actionRequired')}
                    sx={{ backgroundColor: COLORS.orange[100], color: COLORS.orange[900], fontWeight: 600 }}
                  />
                )}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t(content.subtitle)}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.gray[1100], whiteSpace: 'pre-line' }}>
                {t(content.description)}
              </Typography>
              {satNoticeKey && (
                <Typography variant="body2" sx={{ color: COLORS.orange[900], fontWeight: 600 }}>
                  {t(satNoticeKey)}
                </Typography>
              )}
            </Box>
            {action && (
              <Box sx={{ flexShrink: 0 }}>
                <Button endIcon="arrowRight" isFullHeight={false} onClick={action.onClick}>
                  {action.label}
                </Button>
              </Box>
            )}
          </Grid>
          {stage === OnboardingStage.Terms && (
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckboxField
                  label=""
                  name="confirmAndAcceptTermsAndConditions"
                  value={acceptedTerms}
                  onChange={(checked) => {
                    setAcceptedTerms(checked);
                    logEvent(bankingEvents.CLICK_CONFIRM_AND_ACCEPT_TERMS_AND_CONDITIONS, {
                      'Confirm and Accept Terms and Conditions': checked ? 'true' : 'false',
                      'Document URL': termsUrl,
                    });
                  }}
                  fullWidth={false}
                />
                <Typography sx={{ marginLeft: '-1em' }}>
                  <Trans
                    i18nKey="banking-page:bankingApplication.info.banking.checkbox"
                    components={{ a: <TermsLink href={termsUrl} /> }}
                  />
                </Typography>
              </Box>
              <Button
                endIcon="arrowRight"
                isFullHeight={false}
                onClick={handleStartBanking}
                isLoading={isStartingBanking}
                disabled={!acceptedTerms}
              >
                {t('banking-page:onboardingTracker.terms.cta')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Section>
    </Grid>
  );
};

export default OnboardingTracker;
